// Add or edit one shop connection: platform, access, what the shop is used
// for (each use with the exact rights it needs), order options. A Shopify shop
// is connected by installing the Unanimity app on it (Shopify's own page) —
// nobody types a Shopify token; PrestaShop uses its Webservice key.

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaShopify, FaStore } from 'react-icons/fa';
import { CATALOG_USES, ORDER_USES, REQUIRES, RIGHTS, create, fail, update } from './api';

const BACKFILL_OPTIONS = [0, 7, 30, 60];

const initialForm = (shop, meta) => ({
    platform: shop?.platform || 'shopify',
    name: shop?.name || '',
    client_ref: shop?.client_ref || '',
    enabled: shop ? Boolean(shop.enabled) : true,
    uses: { orders: true, notes: false, tracking: false, products: false, prices: false, ...(shop?.uses || {}) },
    shop_domain: shop?.shop_domain || '',
    base_url: shop?.base_url || '',
    webservice_key: '',
    language_id: shop?.language_id || '1',
    vat_rate: shop?.vat_rate ?? 20,
    backfill_days: shop?.backfill_days ?? meta.limits?.default_backfill_days ?? 30,
    ship_from_postcode: shop?.ship_from_postcode || '',
    ship_from_country: shop?.ship_from_country || '',
});

const UseToggle = ({ use, form, setUse, t }) => {
    const needed = REQUIRES[use];
    const blocked = needed && !form.uses[needed];
    return (
        <label className={`shops-use ${blocked ? 'shops-use--blocked' : ''}`}>
            <input type="checkbox" checked={Boolean(form.uses[use]) && !blocked} disabled={blocked}
                onChange={(e) => setUse(use, e.target.checked)} />
            <span>
                <strong>{t(`shops.uses.${use}.label`, use)}</strong>
                <small>{t(`shops.uses.${use}.hint`, '')}</small>
                <code>{RIGHTS[form.platform][use]}</code>
            </span>
        </label>
    );
};

const ShopForm = ({ shop, meta, onSaved, onCancel }) => {
    const { t } = useTranslation();
    const [form, setForm] = useState(() => initialForm(shop, meta));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const editing = Boolean(shop);
    const shopify = form.platform === 'shopify';

    const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
    const setUse = (use, on) => setForm((prev) => {
        const uses = { ...prev.uses, [use]: on };
        Object.entries(REQUIRES).forEach(([dependent, needed]) => { if (!uses[needed]) uses[dependent] = false; });
        return { ...prev, uses };
    });

    const credentialsOk = shopify
        ? meta.shopify_install_available && form.shop_domain.trim().length > 2
        : form.base_url.trim().startsWith('https://') && (form.webservice_key.trim() || (shop?.has_secret && shop?.base_url === form.base_url.trim().replace(/\/+$/, '')));
    const anyUse = Object.values(form.uses).some(Boolean);
    const ready = form.name.trim().length > 1 && credentialsOk && anyUse;

    const submit = async (e) => {
        e.preventDefault();
        if (!ready) return;
        const body = {
            platform: form.platform, name: form.name.trim(), client_ref: form.client_ref.trim() || null, enabled: form.enabled,
            uses: form.uses, backfill_days: Number(form.backfill_days),
            ship_from_postcode: form.ship_from_postcode.trim() || null, ship_from_country: form.ship_from_country.trim() || null,
        };
        if (shopify) {
            body.shop_domain = form.shop_domain.trim();
        } else {
            Object.assign(body, { base_url: form.base_url.trim(), webservice_key: form.webservice_key.trim() || null,
                language_id: form.language_id || '1', vat_rate: Number(form.vat_rate) });
        }
        setBusy(true);
        setError('');
        try {
            const res = editing ? await update(shop.id, body) : await create(body);
            if (res.data.install_url) {
                // Shopify asks the merchant to approve the app (or the new rights).
                window.location.assign(res.data.install_url);
                return;
            }
            onSaved(res.data, { created: !editing });
        } catch (err) {
            setError(fail(err, t('shops.saveError', "La boutique n'a pas pu être enregistrée.")));
        } finally {
            setBusy(false);
        }
    };

    return (
        <form className="shops-form" onSubmit={submit}>
            <header className="shops-form-header">
                <h2>{editing ? t('shops.editTitle', 'Modifier « {{name}} »', { name: shop.name }) : t('shops.addTitle', 'Connecter une boutique')}</h2>
            </header>

            {!editing && (
                <div className="shops-platforms" role="radiogroup" aria-label={t('shops.platform', 'Plateforme')}>
                    {(meta.platforms || []).map((p) => (
                        <button key={p.key} type="button" role="radio" aria-checked={form.platform === p.key}
                            className={`shops-platform ${form.platform === p.key ? 'shops-platform--selected' : ''}`}
                            onClick={() => set({ platform: p.key })}>
                            {p.key === 'shopify' ? <FaShopify aria-hidden="true" /> : <FaStore aria-hidden="true" />}
                            <strong>{p.label}</strong>
                        </button>
                    ))}
                </div>
            )}

            <div className="shops-grid">
                <label className="shops-field">
                    <span>{t('shops.name', 'Nom')}</span>
                    <input value={form.name} maxLength={80} onChange={(e) => set({ name: e.target.value })}
                        placeholder={t('shops.namePlaceholder', 'Boutique FR')} />
                    {editing && <small>{t('shops.key', 'Identifiant')} : <code>{shop.key}</code></small>}
                </label>
                <label className="shops-field">
                    <span>{t('shops.client', 'Client (marchand)')} {t('shops.optional', '(facultatif)')}</span>
                    <input value={form.client_ref} maxLength={128} onChange={(e) => set({ client_ref: e.target.value })}
                        placeholder={t('shops.clientPlaceholder', 'Le marchand de cette boutique, si vous êtes logisticien')} />
                </label>
            </div>

            <fieldset className="shops-fieldset">
                <legend>{t('shops.access', 'Accès à la boutique')}</legend>
                {shopify ? (
                    <>
                        <div className="shops-grid">
                            <label className="shops-field">
                                <span>{t('shops.shopDomain', 'Domaine de la boutique')}</span>
                                <input value={form.shop_domain} onChange={(e) => set({ shop_domain: e.target.value })}
                                    placeholder="maboutique.myshopify.com" disabled={editing} />
                            </label>
                        </div>
                        <p className="shops-note">
                            {meta.shopify_install_available
                                ? t('shops.shopifyInstallHint', 'Vous serez dirigé vers Shopify pour installer l’application Unanimity sur cette boutique et approuver les droits des usages cochés ci-dessous. Aucun jeton à copier.')
                                : t('shops.shopifyUnavailable', 'L’application Shopify n’est pas configurée sur ce serveur : contactez le support.')}
                        </p>
                    </>
                ) : (
                    <>
                        <div className="shops-grid">
                            <label className="shops-field">
                                <span>{t('shops.shopUrl', 'Adresse de la boutique')}</span>
                                <input type="url" value={form.base_url} onChange={(e) => set({ base_url: e.target.value })} placeholder="https://www.maboutique.fr" />
                            </label>
                            <label className="shops-field">
                                <span>{t('shops.webserviceKey', 'Clé Webservice')}</span>
                                <input type="password" autoComplete="off" value={form.webservice_key} onChange={(e) => set({ webservice_key: e.target.value })}
                                    placeholder={shop?.has_secret ? t('shops.keepSecret', 'Laisser vide pour conserver') : ''} />
                            </label>
                            <label className="shops-field shops-field--narrow">
                                <span>{t('shops.languageId', 'ID langue')}</span>
                                <input inputMode="numeric" value={form.language_id} onChange={(e) => set({ language_id: e.target.value.replace(/\D/g, '') || '1' })} />
                            </label>
                        </div>
                        <p className="shops-note">{t('shops.prestashopHint', 'Paramètres avancés › Webservice : activez le Webservice, créez une clé et cochez les droits des usages choisis ci-dessous. Aucun module à installer.')}</p>
                    </>
                )}
            </fieldset>

            <fieldset className="shops-fieldset">
                <legend>{t('shops.usesTitle', 'Ce qu’Unanimity fait avec cette boutique')}</legend>
                <div className="shops-uses">
                    <div>
                        <h3>{t('shops.groupOrders', 'Commandes et colis')}</h3>
                        {ORDER_USES.map((use) => <UseToggle key={use} use={use} form={form} setUse={setUse} t={t} />)}
                    </div>
                    {meta.catalog_enabled && (
                        <div>
                            <h3>{t('shops.groupCatalog', 'Catalogue produits')}</h3>
                            {CATALOG_USES.map((use) => <UseToggle key={use} use={use} form={form} setUse={setUse} t={t} />)}
                        </div>
                    )}
                </div>
            </fieldset>

            {form.uses.orders && (
                <fieldset className="shops-fieldset">
                    <legend>{t('shops.ordersOptions', 'Import des commandes')}</legend>
                    <div className="shops-grid">
                        <label className="shops-field">
                            <span>{t('shops.backfill', 'Première importation')}</span>
                            <select value={form.backfill_days} onChange={(e) => set({ backfill_days: Number(e.target.value) })}>
                                {BACKFILL_OPTIONS.map((d) => (
                                    <option key={d} value={d}>{d === 0 ? t('shops.backfillNone', 'Nouvelles commandes seulement') : t('shops.backfillDays', '{{days}} derniers jours', { days: d })}</option>
                                ))}
                            </select>
                        </label>
                        <label className="shops-field">
                            <span>{t('shops.shipFromPostcode', 'Code postal d’expédition')}</span>
                            <input maxLength={20} value={form.ship_from_postcode} onChange={(e) => set({ ship_from_postcode: e.target.value })} placeholder="75012" />
                        </label>
                        <label className="shops-field shops-field--narrow">
                            <span>{t('shops.shipFromCountry', 'Pays')}</span>
                            <input maxLength={2} value={form.ship_from_country} onChange={(e) => set({ ship_from_country: e.target.value.toUpperCase() })} placeholder="FR" />
                        </label>
                    </div>
                    <p className="shops-note">{t('shops.shipFromHint', 'L’entrepôt d’où partent les colis : la surveillance colis s’en sert pour évaluer chaque colis (les boutiques ne le transmettent pas).')}</p>
                </fieldset>
            )}
            {!shopify && form.uses.products && (
                <label className="shops-field shops-field--narrow">
                    <span>{t('shops.vat', 'TVA (%) pour le prix TTC')}</span>
                    <input inputMode="decimal" value={form.vat_rate} onChange={(e) => set({ vat_rate: e.target.value.replace(/[^\d.]/g, '') })} />
                </label>
            )}

            <label className="shops-check">
                <input type="checkbox" checked={form.enabled} onChange={(e) => set({ enabled: e.target.checked })} />
                <span>{t('shops.enabled', 'Connexion active')}</span>
            </label>

            {error && <p className="shops-error" role="alert">{error}</p>}
            <div className="shops-actions">
                <button type="submit" className="u-btn u-btn--primary" disabled={!ready || busy}>
                    {busy ? t('shops.saving', 'Enregistrement…')
                        : editing ? t('shops.save', 'Enregistrer')
                            : shopify ? t('shops.installShopify', 'Installer l’application sur Shopify') : t('shops.connect', 'Connecter et tester')}
                </button>
                <button type="button" className="u-btn u-btn--secondary" onClick={onCancel} disabled={busy}>{t('shops.cancel', 'Annuler')}</button>
            </div>
        </form>
    );
};

export default ShopForm;
